import React, { useState, useEffect, useRef } from "react";
import { api } from "@/lib/api";
import { Card, Field, Badge, SectionTitle } from "@/components/ui-hud";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ShieldCheck, User as UserIcon, MapPin, Plane, Award, X as XIcon, Plus, Search, Sparkles, FileText, Camera, Loader2, ExternalLink, Check, Star as StarIcon } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import RoleProfileForm, { roleHasProfile, roleLabel } from "@/components/RoleProfileForm";
import TypeRatingDetail, { computeCurrency, CURRENCY_TONE } from "@/components/TypeRatingDetail";
import ProfileChecklist from "@/components/ProfileChecklist";
import AirportInput from "@/components/AirportInput";
import PhotoGallery from "@/components/PhotoGallery";
import { Switch } from "@/components/ui/switch";
import { ArrowLeftRight, Briefcase, Radio as RadioIcon, Lock, ArrowRight, Eye, ChevronDown } from "lucide-react";
import { Link } from "react-router-dom";
import { isPro } from "@/lib/permissions";

// Color pilots' type ratings by OEM so they read like the ID card
const OEM_COLORS = {
  Gulfstream:            { bg: "rgba(212,179,102,0.14)", border: "rgba(212,179,102,0.55)", text: "#D4B366" },
  Bombardier:            { bg: "rgba(62,207,181,0.14)",  border: "rgba(62,207,181,0.55)",  text: "#3ECFB5" },
  Cessna:                { bg: "rgba(75,156,255,0.14)",  border: "rgba(75,156,255,0.55)",  text: "#4B9CFF" },
  Embraer:               { bg: "rgba(139,127,232,0.14)", border: "rgba(139,127,232,0.55)", text: "#8B7FE8" },
  "Dassault Falcon":     { bg: "rgba(255,122,90,0.14)",  border: "rgba(255,122,90,0.55)",  text: "#FF7A5A" },
  "Beechcraft / Hawker": { bg: "rgba(109,190,143,0.14)", border: "rgba(109,190,143,0.55)", text: "#6DBE8F" },
  Pilatus:               { bg: "rgba(245,200,66,0.14)",  border: "rgba(245,200,66,0.55)",  text: "#F5C842" },
  Boeing:                { bg: "rgba(235,87,87,0.14)",   border: "rgba(235,87,87,0.55)",   text: "#EB5757" },
  Airbus:                { bg: "rgba(94,114,228,0.14)",  border: "rgba(94,114,228,0.55)",  text: "#8B96F5" },
  HondaJet:              { bg: "rgba(242,110,165,0.14)", border: "rgba(242,110,165,0.55)", text: "#F26EA5" },
};
const DEFAULT_COLOR = { bg: "rgba(75,156,255,0.14)", border: "rgba(75,156,255,0.55)", text: "#4B9CFF" };

function colorForCode(code, catalog) {
  for (const [oem, items] of Object.entries(catalog || {})) {
    if (items.some((x) => x.code === code)) return OEM_COLORS[oem] || DEFAULT_COLOR;
  }
  return DEFAULT_COLOR;
}

// FAA cert-level codes → readable label
const CERT_LEVEL_LABEL = {
  A: "ATP", C: "COMMERCIAL", P: "PRIVATE", R: "RECREATIONAL", S: "STUDENT", F: "CFI",
};
// FAA rating codes → readable label
const RATING_LABEL = {
  ASEL: "Single-Engine Land", ASES: "Single-Engine Sea",
  AMEL: "Multi-Engine Land",  AMES: "Multi-Engine Sea",
  HEL:  "Rotorcraft — Helicopter", GYRO: "Gyroplane",
  GLI:  "Glider", IR: "Instrument", "IR-AIRPLANE": "Instrument — Airplane",
};

export default function Profile({ embedded = false, viewAs = null, onViewAs = null, availableRoles = null }) {
  const { user, refresh } = useAuth();
  const [form, setForm] = useState({
    first_name: user?.first_name || "",
    last_name: user?.last_name || "",
    nickname: user?.nickname || "",
    bio: user?.bio || "",
    location: user?.location || "",
    location_icao: user?.location_icao || "",
    total_hours: user?.total_hours || 0,
    ratings: user?.ratings || [],
    aircraft_types: user?.aircraft_types || [],
    pilot_certificate_number: user?.pilot_certificate_number || "",
    available_to_work: !!user?.available_to_work,
    share_live_location: !!user?.share_live_location,
    directory_visible: user?.directory_visible !== false,   // default: visible
  });
  const [verifying, setVerifying] = useState(false);
  const [matches, setMatches] = useState(null);
  const [catalog, setCatalog] = useState({});
  const [oemSel, setOemSel] = useState("");
  const [modelSel, setModelSel] = useState("");
  const [drafting, setDrafting] = useState(false);
  const [certs, setCerts] = useState(user?.certificates || []);
  const [roleProfile, setRoleProfile] = useState(user?.role_profile || {});
  const [pendingRemove, setPendingRemove] = useState(null);
  const [resyncing, setResyncing] = useState(false);
  const [typeData, setTypeData] = useState({});         // { "G-V": { last_training_date, currency, ... } }
  const [editingType, setEditingType] = useState(null); // code string when modal open
  const [avatarUploading, setAvatarUploading] = useState(false);
  const avatarRef = useRef(null);
  const avatarCameraRef = useRef(null);
  const [pjpIdToken, setPjpIdToken] = useState(null);
  const [pjpIdData, setPjpIdData] = useState(null);

  // Auto-provision Digital ID token (idempotent) and preload badge data for the mini-card preview.
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    api.post("/id/mine/enable", null, { silent402: true }).then((r) => {
      if (cancelled) return;
      const tok = r.data.public_token;
      setPjpIdToken(tok);
      return api.get(`/id/${tok}`);
    }).then((r) => {
      if (r && !cancelled) setPjpIdData(r.data);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [user?.id]);

  // Load per-type training data
  useEffect(() => {
    if (!user) return;
    api.get("/pilot/type-rating-data").then((r) => setTypeData(r.data.types || {})).catch(() => {});
  }, [user?.id, user?.updated_at]);

  const uploadAvatar = async (file) => {
    if (!file) return;
    setAvatarUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("kind", "avatar");
      const up = await api.post("/files/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
      await api.post("/auth/avatar", { file_id: up.data.id });
      await refresh();
      toast.success("Profile picture updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Avatar upload failed");
    } finally {
      setAvatarUploading(false);
      if (avatarRef.current) avatarRef.current.value = "";
      if (avatarCameraRef.current) avatarCameraRef.current.value = "";
    }
  };

  const onTypeSaved = (code, updated) => {
    setTypeData((prev) => ({ ...prev, [code]: { ...updated.data, currency: updated.currency } }));
  };

  // Re-sync local form state whenever the user object arrives / changes.
  useEffect(() => {
    if (!user) return;
    setForm({
      first_name: user.first_name || "",
      last_name: user.last_name || "",
      nickname: user.nickname || "",
      bio: user.bio || "",
      location: user.location || "",
      location_icao: user.location_icao || "",
      total_hours: user.total_hours || 0,
      ratings: user.ratings || [],
      aircraft_types: user.aircraft_types || [],
      pilot_certificate_number: user.pilot_certificate_number || "",
      additional_roles: user.additional_roles || [],
      available_to_work: !!user.available_to_work,
      share_live_location: !!user.share_live_location,
      directory_visible: user.directory_visible !== false,
    });
    setRoleProfile(user.role_profile || {});
    setCerts(user.certificates || []);
  }, [user?.id, user?.updated_at]);

  useEffect(() => {
    api.get("/faa/type-ratings/catalog").then((r) => setCatalog(r.data)).catch(() => {});
  }, []);

  if (!user) return <div className="max-w-4xl mx-auto px-6 py-12">Auth required</div>;

  const save = async (e) => {
    e.preventDefault();
    if (!form.first_name?.trim() || !form.last_name?.trim()) {
      toast.error("First and last name required");
      return;
    }
    try {
      await api.put("/auth/profile", {
        ...form,
        total_hours: parseInt(form.total_hours) || 0,
        role_profile: roleProfile,
      });
      await refresh();
      toast.success("Profile updated");
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const runLookup = async (overrides = null) => {
    setVerifying(true);
    setMatches(null);
    try {
      const fn = overrides?.first_name ?? form.first_name;
      const ln = overrides?.last_name ?? form.last_name;
      const r = await api.post("/faa/verify", { first_name: fn, last_name: ln });
      if (r.data.status === "matches_found") {
        if (r.data.match_count === 1) {
          await pickMatch(r.data.matches[0].unique_id);
        } else {
          setMatches(r.data.matches);
        }
      } else if (r.data.status === "no_faa_record") {
        // If the pilot's account first_name (probably a nickname) didn't
        // hit and we have an OCR-verified legal name that differs, offer
        // a one-tap retry with the legal name.
        const legalFirst = user?.identity_legal_first;
        const legalLast = user?.identity_legal_last;
        const usedLegalAlready = !overrides
          ? (fn?.toLowerCase() === (legalFirst || "").toLowerCase())
          : true;
        if (!usedLegalAlready && legalFirst && legalLast) {
          toast(
            <div className="text-[13px]">
              <div className="font-semibold mb-1">No FAA record for {fn} {ln}.</div>
              <div className="text-white/70 mb-2">Try your ID legal name: <b>{legalFirst} {legalLast}</b>?</div>
              <button
                onClick={() => {
                  toast.dismiss();
                  runLookup({ first_name: legalFirst, last_name: legalLast });
                }}
                className="mono text-[10px] tracking-widest uppercase px-2.5 py-1 rounded bg-cyan text-obsidian font-bold"
              >
                Retry with legal name
              </button>
            </div>,
            { duration: 8000 }
          );
        } else {
          toast.warning("No FAA record found — you can enter your details manually.");
        }
      } else {
        toast.info("FAA data not yet loaded — verification queued.");
      }
    } catch (e) { toast.error("Lookup failed"); }
    finally { setVerifying(false); }
  };

  const swapLegalName = async () => {
    if (!window.confirm(
      "Swap your first and last names? Use this if OCR extracted them in the wrong order from your ID."
    )) return;
    try {
      await api.post("/auth/profile/swap-name");
      await refresh();
      toast.success("Legal name swapped");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Swap failed");
    }
  };

  const draftBio = async () => {
    setDrafting(true);
    try {
      const r = await api.post("/ai/pilot-bio", { style: "professional" });
      setForm((f) => ({ ...f, bio: r.data.bio }));
      toast.success("Bio drafted by AeroIntel");
    } catch (e) { toast.error("Bio draft failed"); }
    finally { setDrafting(false); }
  };

  const pickMatch = async (unique_id) => {
    try {
      const r = await api.post("/faa/select", { unique_id });
      setMatches(null);
      setCerts(r.data.certificates || []);
      setForm((f) => ({
        ...f,
        first_name: (r.data.first_middle_name || f.first_name).split(" ")[0] || f.first_name,
        aircraft_types: r.data.type_ratings || [],
      }));
      await refresh();
      toast.success(`Verified · ${r.data.type_ratings.length} type ratings loaded`);
    } catch (e) { toast.error("Selection failed"); }
  };

  const askRemoveType = (t) => setPendingRemove(t);
  const removeType = askRemoveType; // legacy alias — now goes through confirm modal

  const cancelRemove = () => setPendingRemove(null);
  const confirmRemove = () => {
    if (!pendingRemove) return;
    setForm((f) => ({ ...f, aircraft_types: f.aircraft_types.filter((x) => x !== pendingRemove) }));
    setPendingRemove(null);
    toast.success("Removed. Hit Re-sync from FAA on the left to bring it back.");
  };

  const resyncFAA = async () => {
    setResyncing(true);
    try {
      const r = await api.post("/faa/resync");
      await refresh();
      toast.success(`Restored ${r.data.count} type rating${r.data.count === 1 ? "" : "s"} from FAA`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Re-sync failed");
    } finally {
      setResyncing(false);
    }
  };

  const addType = () => {
    if (!modelSel) return;
    const opt = (catalog[oemSel] || []).find((o) => o.code === modelSel);
    if (!opt) return;
    if (form.aircraft_types.includes(opt.code)) return;
    setForm((f) => ({ ...f, aircraft_types: [...f.aircraft_types, opt.code] }));
    setModelSel("");
  };

  return (
    <div className={embedded ? "pt-4 pb-8" : "max-w-5xl mx-auto px-6 py-12"}>
      {embedded ? (
        <div className="mb-6">
          <div className="mono text-[10px] tracking-[0.24em] uppercase text-cyan/80">// COCKPIT · PROFILE</div>
          <div className="font-display text-2xl md:text-3xl font-bold text-white tracking-tight mt-1">
            {user.nickname ? user.nickname : `${user.first_name} ${user.last_name}`}
          </div>
          <div className="text-sm text-white/55 mt-0.5">
            {user.nickname
              ? `Legal name: ${user.first_name} ${user.last_name}`
              : `${roleLabel(user.role) || "Pilot"} · Your mission-control profile`}
          </div>
        </div>
      ) : (
        <SectionTitle
          overline="// COCKPIT"
          title={user.nickname ? `${user.nickname}` : `${user.first_name} ${user.last_name}`}
          subtitle={user.nickname ? `Legal name: ${user.first_name} ${user.last_name}` : `${roleLabel(user.role) || "Pilot"} · Your mission-control profile`}
        />
      )}

      <div className="grid md:grid-cols-3 gap-5">
        {/* ============ FAA card — pilot only ============ */}
        {user.role === "pilot" && (
        <Card testId="faa-panel" className="md:col-span-1 !border-cyan/50 relative !bg-gradient-to-br !from-cyan/[0.08] !via-obsidian/95 !to-obsidian/95 shadow-[0_0_50px_-12px_rgba(75,156,255,0.5)]">
          {/* Ambient glow layer */}
          <div
            className="absolute inset-0 pointer-events-none rounded-2xl"
            style={{ background: "radial-gradient(circle at 100% 0%, rgba(75,156,255,0.15), transparent 55%), radial-gradient(circle at 0% 100%, rgba(46,213,115,0.10), transparent 55%)" }}
          />
          {/* Animated top scan line */}
          <div className="absolute inset-x-0 top-0 h-px overflow-hidden rounded-t-2xl">
            <div className="h-full w-1/3 bg-gradient-to-r from-transparent via-cyan to-transparent animate-[scanline_3.6s_linear_infinite]" />
          </div>
          {/* HUD corners */}
          <span className="hud-c-tl" /><span className="hud-c-tr" />
          <span className="hud-c-bl" /><span className="hud-c-br" />

          <div className="relative">
          <div className="mono text-[10px] tracking-[0.28em] uppercase text-cyan mb-2 flex items-center gap-2">
            <span className="relative inline-flex h-1.5 w-1.5">
              <span className="absolute inline-flex h-full w-full rounded-full bg-cyan opacity-70 animate-ping" />
              <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-cyan" />
            </span>
            // MEMBER · IDENTITY
          </div>
          <div className="flex items-center gap-3 mb-4">
            <ShieldCheck className={`h-6 w-6 ${user.faa_verified ? "text-avail" : "text-white/40"}`} />
            <div className="font-display text-xl md:text-2xl font-semibold tracking-tight">FAA Verification</div>
          </div>

          {/* ============ Identity (photo ID) verification ============ */}
          <div className="mb-5 p-3 rounded-lg border border-white/[0.08] bg-white/[0.02]" data-testid="profile-identity-block">
            <div className="flex items-center justify-between gap-2 mb-2">
              <div className="mono text-[10px] tracking-[0.24em] uppercase text-cyan/70">// PHOTO ID</div>
              {user.identity_verified ? (
                <span className="inline-flex items-center gap-1 mono text-[9px] tracking-widest uppercase text-avail bg-avail/10 border border-avail/40 px-1.5 py-0.5 rounded-full" data-testid="profile-identity-verified-badge">
                  <ShieldCheck className="h-3 w-3" /> Verified
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 mono text-[9px] tracking-widest uppercase text-orange-hud bg-orange-hud/10 border border-orange-hud/40 px-1.5 py-0.5 rounded-full" data-testid="profile-identity-pending-badge">
                  Not Verified
                </span>
              )}
            </div>
            {user.identity_verified ? (
              <div className="space-y-1 text-[12px]">
                <div className="text-white/85">
                  <span className="text-white/50">Legal name:</span>{" "}
                  {user.identity_legal_first || user.first_name} {user.identity_legal_last || user.last_name}
                </div>
                <div className="text-white/60">
                  <span className="text-white/45">Document:</span>{" "}
                  <span className="capitalize">{(user.identity_doc_type || "id").replace(/_/g, " ")}</span>
                  {user.identity_verified_at && (
                    <> · <span className="text-white/45">Verified:</span> {new Date(user.identity_verified_at).toLocaleDateString()}</>
                  )}
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                <div className="text-[12px] text-white/70 leading-snug">
                  Required to post, comment, DM, and apply to jobs. Upload once — verification is instant.
                </div>
                <a
                  href="/verify/identity"
                  data-testid="profile-identity-cta"
                  className="inline-flex items-center gap-1.5 mono text-[10px] tracking-widest uppercase text-orange-hud border border-orange-hud/40 hover:border-orange-hud hover:bg-orange-hud/[0.12] px-2.5 py-1.5 rounded-full transition-colors"
                >
                  Verify My ID <ArrowRight className="h-3 w-3" />
                </a>
              </div>
            )}
          </div>

          {user.faa_verified ? (
            <>
              <Badge tone="avail" pulse>Verified Airman</Badge>
              <div className="mt-5 space-y-3">
                <div>
                  <div className="eyebrow-muted mb-1">Cross-checked</div>
                  <div className="mono text-sm">FAA Airmen Registry</div>
                </div>
                <div>
                  <div className="eyebrow-muted mb-1">FAA Airman ID</div>
                  <div className="mono text-sm text-sky">{user.faa_certificate || "—"}</div>
                  <div className="text-[11px] text-white/45 mt-1">FAA's internal identifier used to link your record. Not shown on your Digital ID.</div>
                </div>
                <Field label="Pilot Certificate #">
                  <input
                    data-testid="input-pilot-cert"
                    className="input"
                    value={form.pilot_certificate_number}
                    onChange={(e) => setForm({ ...form, pilot_certificate_number: e.target.value })}
                    placeholder="e.g., 2822744 (from your license)"
                  />
                  <div className="mt-1 text-[11px] text-white/45">The number printed on your pilot certificate. Shown on your PJP Digital ID.</div>
                </Field>
                <button
                  type="button"
                  data-testid="btn-faa-resync"
                  onClick={resyncFAA}
                  disabled={resyncing}
                  className="btn w-full mt-2"
                  title="Repull your type ratings from the FAA snapshot"
                >
                  <Search className="h-3.5 w-3.5" />
                  {resyncing ? "Re-syncing…" : "Re-sync from FAA"}
                </button>
                <div className="text-[11px] text-white/45 -mt-1">Restores any type ratings you accidentally removed.</div>
              </div>
            </>
          ) : (
            <>
              <Badge tone="neutral">Not verified</Badge>
              <p className="text-sm text-white/60 mt-3 mb-4">
                We cross-check your name against the FAA Airmen Registry (591K+ pilots) and auto-load your type ratings.
              </p>
              <button data-testid="btn-verify-faa" onClick={runLookup} disabled={verifying} className="btn btn-primary w-full">
                <Search className="h-3.5 w-3.5" />{verifying ? "Searching FAA…" : "Run FAA Cross-Check"}
              </button>
            </>
          )}

          {matches && matches.length > 1 && (
            <div className="mt-5">
              <div className="eyebrow-muted mb-2">Multiple matches — pick you</div>
              <div className="space-y-2">
                {matches.map((m) => (
                  <button key={m.unique_id} onClick={() => pickMatch(m.unique_id)}
                    className="w-full text-left p-3 rounded-lg border border-white/10 hover:border-sky/60 hover:bg-sky/5 transition-colors">
                    <div className="text-sm text-white">{(m.first_middle_name || "").toLowerCase().replace(/\b\w/g, c=>c.toUpperCase())} {(m.last_name_suffix || "").toLowerCase().replace(/\b\w/g, c=>c.toUpperCase())}</div>
                    <div className="mono text-[11px] text-white/50">{m.city}, {m.state}</div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* ============ Live PJP Digital ID badge — inside FAA card, ID-card sized ============ */}
          <div className="mt-6 pt-6 border-t border-white/[0.08]" data-testid="profile-mini-id-block">
            <div className="flex items-center justify-between gap-2 mb-3">
              <div className="mono text-[10px] tracking-[0.24em] text-sky/70 uppercase">// Your PJP ID</div>
              {pjpIdToken && (
                <a
                  href={`/id/${pjpIdToken}`}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="btn-open-full-badge"
                  className="inline-flex items-center gap-1 mono text-[10px] tracking-widest text-sky hover:text-white transition-colors"
                >
                  FULL VIEW <ExternalLink className="h-3 w-3" />
                </a>
              )}
            </div>
            {pjpIdToken ? (
              <div className="flex justify-center">
                <div
                  className="rounded-xl overflow-hidden border border-white/10"
                  style={{ background: "#050810", width: 240, height: 480 }}
                >
                  <iframe
                    data-testid="profile-digital-id-iframe"
                    src={`/id/${pjpIdToken}?embed=1`}
                    title="PJP Digital ID"
                    loading="lazy"
                    scrolling="no"
                    className="block pointer-events-none"
                    style={{
                      width: 480,
                      height: 960,
                      border: 0,
                      background: "transparent",
                      transform: "scale(0.5)",
                      transformOrigin: "top left",
                      overflow: "hidden",
                    }}
                  />
                </div>
              </div>
            ) : (
              <div className="rounded-xl border border-white/10 bg-white/[0.02] py-10 grid place-items-center text-white/50">
                <Loader2 className="h-4 w-4 animate-spin mb-2" />
                <div className="mono text-[9px] tracking-widest">PROVISIONING BADGE…</div>
              </div>
            )}
          </div>

          {/* ============ Pilot History Form CTA — insurance-ready ============ */}
          <div className="mt-6 pt-6 border-t border-white/[0.08]" data-testid="profile-phf-block">
            <div className="mono text-[10px] tracking-[0.24em] text-sky/70 uppercase mb-2">// Insurance</div>
            <div className="text-[13px] text-white/70 mb-3 leading-snug">
              Complete your Pilot History Form to be added as additionally insured on client aircraft and pre-approved for trip requests.
            </div>
            <a
              href="/phf"
              data-testid="btn-open-phf"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg mono text-[10px] tracking-widest text-white bg-sky/15 border border-sky/40 hover:bg-sky/25 transition-colors"
            >
              <FileText className="h-3.5 w-3.5" /> Open PHF Form
            </a>

            {/* Mini onboarding checklist — sized to sit inside the PHF box
                so the Cockpit doesn't render a bulky duplicate below. */}
            <div className="mt-4">
              <ProfileChecklist mini />
            </div>
          </div>
          </div>
        </Card>
        )}

        {/* ============ Details ============ */}
        <Card testId="profile-form" className={user.role === "pilot" ? "md:col-span-2" : "md:col-span-3"}>
          <div className="font-display text-xl font-semibold mb-6 flex items-center gap-3"><UserIcon className="h-5 w-5 text-sky" /> Profile Details</div>

          {/* Onboarding checklist.
              • Non-embedded (standalone /profile): full CAS checklist.
              • Embedded (Cockpit) for non-pilots: mini checklist inline here
                since there's no FAA card to host it.
              Pilots in embedded mode already see the mini checklist inside
              the FAA card next to PHF. */}
          {!embedded ? (
            <div className="mb-6">
              <ProfileChecklist title="Complete your PJP profile" />
            </div>
          ) : user.role !== "pilot" ? (
            <div className="mb-6">
              <ProfileChecklist mini />
            </div>
          ) : null}

          {/* Avatar upload — mobile-friendly */}
          <div className="mb-6 flex items-center gap-4">
            <div className="relative flex-shrink-0">
              {user.avatar_url ? (
                <img
                  src={user.avatar_url.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL || ""}${user.avatar_url}` : user.avatar_url}
                  alt=""
                  className="h-20 w-20 rounded-full object-cover border-2 border-sky/40"
                  data-testid="profile-avatar-img"
                />
              ) : (
                <div className="h-20 w-20 rounded-full bg-gradient-to-br from-sky/40 to-sky/10 border-2 border-sky/40 grid place-items-center mono text-xl font-bold text-white">
                  {(user.first_name || "?").charAt(0)}{(user.last_name || "").charAt(0)}
                </div>
              )}
              {avatarUploading && (
                <div className="absolute inset-0 rounded-full bg-black/60 grid place-items-center">
                  <Loader2 className="h-6 w-6 text-sky animate-spin" />
                </div>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-white truncate">
                {user.nickname || `${user.first_name} ${user.last_name}`}
              </div>
              {user.nickname && (
                <div className="mono text-[10px] text-white/45 truncate">{user.first_name} {user.last_name}</div>
              )}
              <div className="mono text-[11px] text-white/50 uppercase tracking-wider">Profile Picture</div>
              <input
                ref={avatarRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => uploadAvatar(e.target.files?.[0])}
                data-testid="input-avatar-file"
              />
              <input
                ref={avatarCameraRef}
                type="file"
                accept="image/*"
                capture="user"
                className="hidden"
                onChange={(e) => uploadAvatar(e.target.files?.[0])}
                data-testid="input-avatar-camera"
              />
              <div className="mt-2 flex flex-wrap gap-1.5">
                <button
                  type="button"
                  onClick={() => avatarCameraRef.current?.click()}
                  disabled={avatarUploading}
                  data-testid="btn-avatar-camera"
                  className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-xs font-semibold bg-cyan text-obsidian hover:brightness-110 min-h-[36px] disabled:opacity-40"
                >
                  <Camera className="h-3.5 w-3.5" />
                  Take Photo
                </button>
                <button
                  type="button"
                  onClick={() => avatarRef.current?.click()}
                  disabled={avatarUploading}
                  data-testid="btn-upload-avatar"
                  className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-xs font-semibold bg-white/[0.04] border border-white/15 text-white hover:border-sky/50 min-h-[36px] disabled:opacity-40"
                >
                  <Plus className="h-3.5 w-3.5" />
                  {user.avatar_url ? "Change" : "Upload"}
                </button>
              </div>
            </div>
          </div>

          {/* Photo gallery — pilots can add ramp/cockpit/personal shots */}
          <PhotoGallery onAvatarChanged={() => refresh()} />

          <form onSubmit={save} className="space-y-5">
            <div className="grid grid-cols-2 gap-3">
              <Field
                label={
                  <div className="flex items-center gap-1.5">
                    Legal First Name
                    {user.documents_lock_name && <ShieldCheck className="h-3 w-3 text-avail" title="Verified from ID" />}
                  </div>
                }
              >
                <input
                  data-testid="p-first"
                  required
                  className="input"
                  value={form.first_name}
                  onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                  disabled={user.documents_lock_name}
                  readOnly={user.documents_lock_name}
                />
              </Field>
              <Field
                label={
                  <div className="flex items-center gap-1.5">
                    Legal Last Name
                    {user.documents_lock_name && <ShieldCheck className="h-3 w-3 text-avail" title="Verified from ID" />}
                  </div>
                }
              >
                <input
                  data-testid="p-last"
                  required
                  className="input"
                  value={form.last_name}
                  onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                  disabled={user.documents_lock_name}
                  readOnly={user.documents_lock_name}
                />
              </Field>
            </div>
            {user.documents_lock_name && (
              <div className="-mt-3 flex flex-wrap items-start gap-3 justify-between">
                <div className="text-[11px] text-white/45 flex-1 min-w-[240px]">
                  Your legal name is <span className="text-avail">verified from your uploaded ID</span> and cannot be edited here.
                  Every PJP member can see this name — pick a nickname below for social display.
                </div>
                <button
                  type="button"
                  data-testid="p-swap-name"
                  onClick={swapLegalName}
                  className="mono text-[10px] tracking-widest uppercase px-3 py-1.5 rounded border border-amber-400/40 text-amber-300 hover:bg-amber-400/10 flex items-center gap-1.5 flex-shrink-0"
                  title="Use this if OCR pulled your first & last name in reverse order"
                >
                  <ArrowLeftRight className="h-3 w-3" /> First/Last Reversed?
                </button>
              </div>
            )}
            <Field
              label="Nickname (optional)"
              helper="How you'd like to be known socially. Your real name is always visible to other members."
            >
              <input
                data-testid="p-nickname"
                className="input"
                value={form.nickname || ""}
                onChange={(e) => setForm({ ...form, nickname: e.target.value })}
                placeholder={`e.g. ${(form.first_name || "Jeff")}y, ${(form.first_name || "Jeff")} the Legend`}
                maxLength={40}
              />
            </Field>
            <Field label="Home base (nearest airport)" helper="Type ICAO/IATA/city to lock in your home base. Used for job matching, empty-leg alerts and nearby crew searches.">
              <AirportInput
                testId="p-loc-icao"
                value={form.location_icao || ""}
                onChange={(icao) => setForm({ ...form, location_icao: icao })}
                placeholder="Type ICAO or city (e.g. KOPF, Miami, KTEB)"
              />
              {!form.location_icao && form.location && (
                <div className="text-[11px] text-white/45 mt-1.5">
                  Currently based near: <span className="text-white/70">{form.location}</span> — pick an airport above to link a live location.
                </div>
              )}
            </Field>

            {/* Work-status switches — Pro-gated. Free users see them dimmed
                with an "Unlock with Pro" mini-CTA underneath. */}
            {(() => {
              const proUser = isPro(user);
              const lockedCta = !proUser && (
                <div className="md:col-span-2 -mt-1 flex items-center justify-between gap-3 rounded-md border border-cyan/25 bg-cyan/[0.06] px-3 py-2" data-testid="work-switches-lock-cta">
                  <div className="flex items-center gap-2 text-[12px] text-white/70">
                    <Lock className="h-3.5 w-3.5 text-cyan flex-shrink-0" />
                    <span>Work-status broadcasting is a <span className="text-cyan font-semibold">PJP Pro</span> perk.</span>
                  </div>
                  <Link
                    to="/membership/addon?tier=pro"
                    data-testid="work-switches-upgrade-btn"
                    className="inline-flex items-center gap-1 mono text-[10px] tracking-widest uppercase text-cyan border border-cyan/40 hover:border-cyan hover:bg-cyan/[0.12] px-2.5 py-1 rounded-full transition-colors whitespace-nowrap"
                  >
                    Unlock with Pro <ArrowRight className="h-3 w-3" />
                  </Link>
                </div>
              );
              return (
                <div className="grid md:grid-cols-2 gap-3" data-testid="p-work-switches">
                  <div className={proUser ? "" : "opacity-50 pointer-events-none select-none"}>
                    <SwitchRow
                      testId="p-available-toggle"
                      icon={Briefcase}
                      title="Available to work"
                      subtitle="Show as taking gigs — operators & FBOs see a green AVAILABLE badge on your profile and in crew search."
                      checked={proUser && !!form.available_to_work}
                      onCheckedChange={(v) => proUser && setForm({ ...form, available_to_work: v })}
                      tone="green"
                      disabled={!proUser}
                    />
                  </div>
                  <div className={proUser ? "" : "opacity-50 pointer-events-none select-none"}>
                    <SwitchRow
                      testId="p-live-loc-toggle"
                      icon={RadioIcon}
                      title="Share live location for work"
                      subtitle="Broadcast your real-time location so nearby AOG requests, empty-leg legs and same-day gigs can find you. You can pause this any time."
                      checked={proUser && !!form.share_live_location}
                      onCheckedChange={(v) => proUser && setForm({ ...form, share_live_location: v })}
                      tone="cyan"
                      disabled={!proUser}
                    />
                  </div>
                  {lockedCta}
                </div>
              );
            })()}

            {/* Directory visibility — always available (not gated by Pro). Defaults ON. */}
            <div className="mt-1" data-testid="p-directory-visibility">
              <SwitchRow
                testId="p-directory-visibility-toggle"
                icon={Eye}
                title="Show me in the public directory"
                subtitle="Let other verified PJP members find you inside your role's directory category. Turn off to hide your profile card (you can still be found via direct DM link)."
                checked={form.directory_visible !== false}
                onCheckedChange={(v) => setForm({ ...form, directory_visible: v })}
                tone="cyan"
              />
            </div>

            {/* Multi-role hat picker — personal roles only. Entity roles (charter/FBO)
                are separate accounts and NOT toggleable here. */}
            <Field label="Hats you wear">
              <div className="input !p-3" data-testid="profile-roles">
                <div className="text-[11px] text-white/45 mb-3">
                  Your <span className="text-cyan">primary</span> role drives your badge & default sections.
                  Extra hats unlock those sections too — one PJP membership covers all of them.
                </div>
                <div className="flex flex-wrap gap-2">
                  {[
                    { key: "pilot",            label: "Pilot" },
                    { key: "flight_attendant", label: "Flight Attendant" },
                    { key: "amt",              label: "AMT (A&P)" },
                    { key: "aircraft_owner",   label: "Aircraft Owner" },
                    { key: "aircraft_sales",   label: "Sales / Broker" },
                    { key: "enthusiast",       label: "AV/EXEC" },
                  ].map((r) => {
                    const isPrimary = user.role === r.key;
                    const isExtra = (form.additional_roles || []).includes(r.key);
                    const isChecked = isPrimary || isExtra;
                    const toggle = () => {
                      if (isPrimary) return; // can't uncheck primary — use MAKE PRIMARY on another chip instead
                      setForm((f) => ({
                        ...f,
                        additional_roles: isExtra
                          ? f.additional_roles.filter((x) => x !== r.key)
                          : [...(f.additional_roles || []), r.key],
                      }));
                    };
                    const makePrimary = async (e) => {
                      e.preventDefault(); e.stopPropagation();
                      try {
                        const nextExtras = [...(form.additional_roles || []).filter((x) => x !== r.key), user.role];
                        await api.put("/auth/profile", { role: r.key, additional_roles: nextExtras });
                        toast.success(`Primary hat is now ${r.label}`);
                        await refresh?.();
                      } catch { toast.error("Couldn't switch primary hat"); }
                    };
                    return (
                      <button
                        key={r.key}
                        type="button"
                        onClick={toggle}
                        data-testid={`profile-role-row-${r.key}`}
                        className={`group inline-flex items-center gap-2 pl-3 pr-2 py-2 rounded-full border text-sm transition-all ${
                          isPrimary
                            ? "border-cyan bg-cyan/[0.18] text-white shadow-[0_0_18px_rgba(75,156,255,0.35)] cursor-default"
                            : isExtra
                              ? "border-cyan/40 bg-cyan/[0.06] text-white"
                              : "border-white/[0.12] bg-white/[0.02] text-white/70 hover:border-white/25 hover:text-white"
                        }`}
                      >
                        <span
                          data-testid={`profile-role-check-${r.key}`}
                          aria-checked={isChecked}
                          role="checkbox"
                          className={`inline-flex h-4 w-4 items-center justify-center rounded-[3px] border ${
                            isChecked ? "bg-cyan border-cyan" : "border-white/40"
                          }`}
                        >
                          {isChecked && (
                            <svg viewBox="0 0 20 20" className="h-3 w-3 text-obsidian" fill="currentColor">
                              <path d="M8.3 13.3l-3-3 1.4-1.4 1.6 1.6 4.6-4.6 1.4 1.4z" />
                            </svg>
                          )}
                        </span>
                        <span className="whitespace-nowrap">{r.label}</span>
                        {isPrimary ? (
                          <span className="ml-1 mono text-[9px] tracking-widest text-cyan inline-flex items-center gap-1">
                            <StarIcon className="h-3 w-3 fill-cyan text-cyan" /> PRIMARY
                          </span>
                        ) : (
                          isExtra && (
                            <span
                              role="button"
                              tabIndex={0}
                              onClick={makePrimary}
                              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") makePrimary(e); }}
                              data-testid={`profile-role-set-primary-${r.key}`}
                              title="Make primary"
                              className="ml-1 inline-flex h-6 w-6 items-center justify-center rounded-full text-white/40 hover:text-cyan hover:bg-white/[0.06] cursor-pointer"
                            >
                              <StarIcon className="h-3.5 w-3.5" />
                            </span>
                          )
                        )}
                      </button>
                    );
                  })}
                </div>
                <div className="mt-3 flex items-start gap-2 rounded-md border border-white/[0.06] bg-white/[0.02] px-3 py-2">
                  <div className="mono text-[9px] tracking-widest text-white/40 mt-0.5">CO / FBO</div>
                  <div className="text-[11px] text-white/55">
                    Charter operators and FBOs use a separate business entity account.
                    Register or manage yours at <a href="/operators/onboard" className="text-cyan hover:text-white">/operators/onboard</a>.
                  </div>
                </div>
              </div>
            </Field>

            {user.role === "pilot" ? (
              <>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Total Hours"><input data-testid="p-hours" type="number" className="input" value={form.total_hours} onChange={(e) => setForm({ ...form, total_hours: e.target.value })} /></Field>
              <Field label="Ratings (comma-separated)"><input data-testid="p-ratings" className="input" value={Array.isArray(form.ratings) ? form.ratings.join(", ") : form.ratings} onChange={(e) => setForm({ ...form, ratings: e.target.value.split(",").map(s=>s.trim()).filter(Boolean) })} placeholder="ATP, ASEL, AMEL" /></Field>
            </div>

            {/* ============ Aircraft Types — chips + OEM picker ============ */}
            <Field label="Aircraft Types (FAA Type Ratings)">
              <div className="input !p-3">
                <div className="flex flex-wrap gap-2 min-h-[36px]">
                  {form.aircraft_types.length === 0 && (
                    <span className="text-white/40 text-sm px-2 py-1">No type ratings yet — run FAA check or add manually below</span>
                  )}
                  {(() => {
                    // Rank type coins by recency: most current recurrent (fewest months since
                    // last training) at spot 1, older next, and any without a recorded
                    // training date bumped to the bottom (alpha-sorted among themselves).
                    const rank = (t) => {
                      const trec = typeData[t] || {};
                      const cur = trec.currency || computeCurrency(trec);
                      const m = cur?.months_since;
                      if (m === null || m === undefined) return Infinity;
                      return m;
                    };
                    return form.aircraft_types.slice().sort((a, b) => {
                      const ra = rank(a), rb = rank(b);
                      if (ra === rb) return a.localeCompare(b);
                      return ra - rb;
                    });
                  })().map((t) => {
                    const trec = typeData[t] || {};
                    const cur = trec.currency || computeCurrency(trec);
                    const tone = CURRENCY_TONE[cur.status] || CURRENCY_TONE.red;
                    const hasData = !!trec.last_training_date;
                    return (
                      <span key={t} data-testid={`type-chip-${t}`}
                        className="inline-flex items-center gap-1.5 pl-2 pr-1 py-1 rounded-full text-[12px] font-semibold tracking-wide mono border transition-transform hover:scale-[1.03]"
                        style={{ background: tone.bg, borderColor: tone.border, color: tone.text }}>
                        <button
                          type="button"
                          data-testid={`btn-open-type-${t}`}
                          onClick={() => setEditingType(t)}
                          className="inline-flex items-center gap-1.5 pl-1 py-0.5"
                          title={`${tone.label}${cur.months_since !== null && cur.months_since !== undefined ? ` · ${cur.months_since} mo` : ""}`}
                        >
                          <span
                            className="h-2 w-2 rounded-full flex-shrink-0"
                            style={{ background: tone.dot, boxShadow: hasData ? `0 0 6px ${tone.dot}` : "none" }}
                          />
                          {t}
                          {cur.needs_recurrent_soon && <span className="ml-0.5 text-[9px] px-1 py-0.5 rounded-full bg-orange-hud/25 text-orange-hud tracking-wider">10MO</span>}
                        </button>
                        <button type="button" onClick={() => askRemoveType(t)} data-testid={`btn-remove-type-${t}`} className="ml-0.5 hover:text-white p-1 rounded-full opacity-70 hover:opacity-100">
                          <XIcon className="h-3 w-3" />
                        </button>
                      </span>
                    );
                  })}
                </div>
                {/* Add-by-OEM dropdown */}
                <div className="mt-3 pt-3 border-t border-white/[0.08] grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-2">
                  <select
                    data-testid="type-oem-select"
                    className="input !py-2 !text-[13px]"
                    value={oemSel}
                    onChange={(e) => { setOemSel(e.target.value); setModelSel(""); }}
                  >
                    <option value="">Select manufacturer…</option>
                    {Object.keys(catalog).sort((a, b) => a.localeCompare(b)).map((oem) => <option key={oem} value={oem}>{oem}</option>)}
                  </select>
                  <select
                    data-testid="type-model-select"
                    className="input !py-2 !text-[13px]"
                    value={modelSel}
                    onChange={(e) => setModelSel(e.target.value)}
                    disabled={!oemSel}
                  >
                    <option value="">Select model…</option>
                    {(catalog[oemSel] || []).slice().sort((a, b) => a.label.localeCompare(b.label)).map((o) => (
                      <option key={o.code} value={o.code}>{o.label} — {o.code}</option>
                    ))}
                  </select>
                  <button type="button" data-testid="type-add-btn" onClick={addType} disabled={!modelSel}
                    className="btn btn-primary btn-sm justify-center">
                    <Plus className="h-3.5 w-3.5" /> Add
                  </button>
                </div>
              </div>
              <div className="mt-1.5 text-[11px] text-white/45">
                Click a chip to add training data. Colors reflect currency: <span className="text-[#4EDF8B]">green</span> = PIC current (0–12 mo), <span className="text-[#FFB566]">orange</span> = SIC (13–23 mo), <span className="text-[#F5C842]">yellow</span> = expiring (24 mo), <span className="text-[#FF7A7A]">red</span> = expired or no data.
              </div>
            </Field>

            {/* ============ Licenses & Ratings — pill containers from FAA data ============ */}
            {(certs && certs.length > 0) && (
              <Field label="Licenses & Ratings (FAA-verified)">
                <div className="input !p-3">
                  <div className="flex flex-wrap gap-2">
                    {certs.map((c, i) => {
                      const level = CERT_LEVEL_LABEL[(c.cert_level || "").toUpperCase()] || c.cert_level;
                      return (
                        <React.Fragment key={i}>
                          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-semibold tracking-wide mono border"
                            style={{ background:"rgba(75,156,255,0.18)", borderColor:"rgba(75,156,255,0.6)", color:"#4B9CFF" }}>
                            {level}
                          </span>
                          {(c.ratings || []).map((rc) => (
                            <span key={rc} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-medium tracking-wide mono border"
                              style={{ background:"rgba(109,190,143,0.12)", borderColor:"rgba(109,190,143,0.45)", color:"#6DBE8F" }}>
                              {RATING_LABEL[rc] || rc}
                            </span>
                          ))}
                        </React.Fragment>
                      );
                    })}
                  </div>
                </div>
                <div className="mt-1.5 text-[11px] text-white/45">Certificate level and category/class ratings pulled directly from the FAA Airmen Registry.</div>
              </Field>
            )}
              </>
            ) : (
              <RoleProfileForm role={user.role} values={roleProfile} onChange={setRoleProfile} />
            )}

            <Field label="Bio">
              <textarea data-testid="p-bio" rows={5} className="input" value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} placeholder="Auto-drafted or write your own — no paperwork required."/>
              <div className="mt-2 flex flex-wrap gap-2 items-center">
                <button type="button" data-testid="btn-draft-bio" onClick={draftBio} disabled={drafting}
                  className="btn btn-sm btn-primary">
                  <Sparkles className="h-3.5 w-3.5" />
                  {drafting ? "Drafting with AeroIntel…" : "Draft with AeroIntel"}
                </button>
                <div className="text-[11px] text-white/45">Uses your FAA-verified type ratings, hours, and location to write a positive, employer-facing bio.</div>
              </div>
            </Field>
            <button data-testid="btn-save-profile" className="btn btn-primary">Save Profile</button>
          </form>
        </Card>

        {/* Stats strip */}
        <Card className="md:col-span-3">
          <div className="grid md:grid-cols-4 gap-6 text-center">
            <RoleStat
              current={viewAs || user.role}
              nativeRole={user.role}
              options={availableRoles && availableRoles.length > 1 ? availableRoles : null}
              onChange={onViewAs}
            />
            <Stat icon={MapPin} label="Based" value={user.location_icao || user.location || "—"} />
            <Stat icon={Plane} label="Hours" value={(user.total_hours || 0).toLocaleString()} />
            <Stat icon={ShieldCheck} label="Status" value={user.faa_verified ? "Verified" : "Pending"} tone={user.faa_verified ? "avail" : "sky"} />
          </div>
        </Card>
      </div>

      {editingType && (
        <TypeRatingDetail
          code={editingType}
          initial={typeData[editingType] || {}}
          onClose={() => setEditingType(null)}
          onSaved={onTypeSaved}
        />
      )}

      {pendingRemove && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center px-6" data-testid="modal-confirm-remove-type">
          <div className="bg-obsidian border border-cyan/30 rounded-2xl max-w-md w-full p-6">
            <div className="overline text-orange-hud mb-2">// REMOVE TYPE RATING</div>
            <h3 className="font-display text-2xl font-bold tracking-tight mb-2">Remove <span className="mono text-sky">{pendingRemove}</span>?</h3>
            <p className="text-sm text-white/60 mb-6">
              This removes it from your searchable profile. You can restore it any time by hitting
              <span className="text-cyan font-semibold"> Re-sync from FAA</span> on the FAA panel.
            </p>
            <div className="flex gap-3 justify-end">
              <button data-testid="btn-cancel-remove" onClick={cancelRemove} className="btn">Cancel</button>
              <button data-testid="btn-confirm-remove" onClick={confirmRemove} className="btn btn-primary" style={{background:"#EB5757", borderColor:"#EB5757"}}>Yes, Remove</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ icon: Icon, label, value, tone = "sky" }) {
  const color = tone === "avail" ? "text-avail" : "text-sky";
  return (
    <div>
      <Icon className={`h-5 w-5 mx-auto ${color}`} />
      <div className="eyebrow-muted mt-2">{label}</div>
      <div className="font-display text-lg mt-1 text-white">{value}</div>
    </div>
  );
}

/**
 * RoleStat — same footprint as the generic Stat tile but the role value is
 * click-to-open when the parent supplies more than one role option (multi-hat
 * users + admins). Selecting a new role fires `onChange(role)` which flips
 * the cockpit view without changing the DB `user.role`.
 */
function RoleStat({ current, nativeRole, options, onChange }) {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef(null);
  React.useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const clickable = !!(options && options.length > 1 && onChange);
  const displayLabel = (roleLabel(current) || current || "").toUpperCase();

  if (!clickable) {
    return <Stat icon={Award} label="Role" value={displayLabel} />;
  }

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid="profile-role-stat-btn"
        aria-expanded={open}
        className="w-full group focus:outline-none"
        title="Tap to switch cockpit view to another of your roles"
      >
        <Award className="h-5 w-5 mx-auto text-sky group-hover:text-cyan transition-colors" />
        <div className="eyebrow-muted mt-2 flex items-center justify-center gap-1">
          Role <ChevronDown className={`h-3 w-3 text-sky transition-transform ${open ? "rotate-180" : ""}`} />
        </div>
        <div className="font-display text-lg mt-1 text-white group-hover:text-cyan transition-colors flex items-center justify-center gap-1.5">
          {displayLabel}
        </div>
      </button>
      {open && (
        <div
          className="absolute z-40 left-1/2 -translate-x-1/2 mt-2 min-w-[220px] rounded-xl overflow-hidden border border-white/15"
          style={{
            background: "linear-gradient(155deg, rgba(75,156,255,0.12) 0%, rgba(5,8,16,0.94) 60%)",
            backdropFilter: "blur(22px) saturate(150%)",
            WebkitBackdropFilter: "blur(22px) saturate(150%)",
            boxShadow: "0 30px 60px -20px rgba(0,0,0,0.7), 0 0 60px -28px rgba(75,156,255,0.55)",
          }}
          data-testid="profile-role-stat-menu"
        >
          <div className="mono text-[9px] tracking-[0.24em] uppercase text-cyan/80 px-3 pt-2.5 pb-1">// SWITCH VIEW</div>
          <div className="p-1.5 space-y-1">
            {options.map((r) => {
              const active = r === current;
              const isNative = r === nativeRole;
              return (
                <button
                  key={r}
                  type="button"
                  onClick={() => { onChange(r); setOpen(false); }}
                  data-testid={`profile-role-stat-opt-${r}`}
                  className={`w-full text-left flex items-center justify-between gap-2 px-3 py-2 rounded-lg text-[13px] transition-colors ${
                    active ? "bg-cyan/[0.14] text-white" : "hover:bg-white/[0.05] text-white/80"
                  }`}
                >
                  <span className="flex items-center gap-2 min-w-0">
                    <span className="font-medium truncate">{roleLabel(r) || r.replace(/_/g, " ")}</span>
                    {isNative && (
                      <span className="mono text-[8.5px] tracking-widest text-white/70 border border-white/20 px-1 rounded flex-shrink-0">OWN</span>
                    )}
                  </span>
                  {active && <span className="h-1.5 w-1.5 rounded-full bg-cyan flex-shrink-0" />}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}




/**
 * MiniIdCard — traditional credit-card-sized preview of the pilot's PJP Digital ID.
 * Landscape orientation, sized to feel like a physical ID (roughly credit card ratio).
 * Photo left, ID meta right, tiny QR bottom-right. Tap to open the full live badge.
 */
function MiniIdCard({ token, data, user }) {
  if (!token) {
    return (
      <div
        className="w-full rounded-xl border border-white/10 bg-white/[0.02] grid place-items-center text-white/50"
        style={{ aspectRatio: "1.586 / 1" }}
      >
        <div className="text-center">
          <Loader2 className="h-4 w-4 animate-spin mx-auto mb-1" />
          <div className="mono text-[9px] tracking-widest">PROVISIONING…</div>
        </div>
      </div>
    );
  }

  const name = data?.name || `${user.first_name} ${user.last_name}`;
  const memberNumber = data?.pjp_member_number ? String(data.pjp_member_number).padStart(5, "0") : "—";
  const certLabel = data?.pilot_certificate_number ? `ATP ${data.pilot_certificate_number}` : (data?.certificates?.[0]?.number ? `ATP ${data.certificates[0].number}` : "—");
  const photo = data?.id_photo_url || data?.avatar_url || user.avatar_url;
  const photoSrc = photo ? (photo.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL || ""}${photo}` : photo) : null;
  const url = `${window.location.origin}/id/${token}`;
  const verified = data?.faa_verified && (data?.membership_status === "active");

  return (
    <a
      href={`/id/${token}`}
      target="_blank"
      rel="noreferrer"
      data-testid="mini-id-card"
      className="block w-full rounded-xl overflow-hidden relative group"
      style={{
        aspectRatio: "1.586 / 1",
        background: "linear-gradient(135deg, #0A0F1A 0%, #101827 100%)",
        border: "1px solid rgba(46,213,115,0.35)",
        boxShadow: "0 0 24px rgba(46,213,115,0.12), inset 0 0 0 1px rgba(255,255,255,0.02)",
      }}
    >
      {/* Verified sliver at top */}
      <div
        className="absolute top-0 inset-x-0 flex items-center justify-center gap-1.5 mono text-[8px] font-bold tracking-[0.2em] py-1"
        style={{ background: verified ? "#2ED573" : "rgba(255,255,255,0.08)", color: verified ? "#0A1F14" : "rgba(255,255,255,0.55)" }}
      >
        <span className="h-1 w-1 rounded-full" style={{ background: verified ? "#0A1F14" : "rgba(255,255,255,0.4)" }} />
        PJP VERIFIED {verified && <Check className="h-2.5 w-2.5" />}
      </div>

      {/* Body */}
      <div className="absolute inset-0 pt-5 pb-2 px-3 grid grid-cols-[36%_1fr] gap-2.5">
        {/* Photo */}
        <div className="relative rounded-md overflow-hidden bg-white/5 border border-white/10">
          {photoSrc ? (
            <img src={photoSrc} alt="" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full grid place-items-center text-white/25">
              <Camera className="h-4 w-4" />
            </div>
          )}
        </div>

        {/* Meta */}
        <div className="min-w-0 flex flex-col justify-between">
          <div>
            <div className="font-display font-bold text-white text-[13px] leading-tight tracking-tight truncate">{name}</div>
            <div className="mono text-[8px] tracking-widest text-white/45 uppercase mt-0.5">PJP-ID {memberNumber}</div>
            <div className="mono text-[9px] text-sky mt-1 truncate">{certLabel}</div>
          </div>
          <div className="flex items-end justify-between gap-2">
            <div className="min-w-0 flex-1">
              <div className="mono text-[7px] tracking-[0.18em] text-white/40 uppercase">Role</div>
              <div className="mono text-[9px] font-bold text-white uppercase">{(data?.role || user.role || "MEMBER")}</div>
            </div>
            <div className="bg-white p-0.5 rounded flex-shrink-0" title="Scan to verify">
              <QRCodeSVG value={url} size={30} bgColor="#ffffff" fgColor="#050810" />
            </div>
          </div>
        </div>
      </div>

      {/* Hover CTA */}
      <div
        className="absolute inset-0 grid place-items-center opacity-0 group-hover:opacity-100 transition-opacity"
        style={{ background: "rgba(5,8,16,0.75)" }}
      >
        <div className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 mono text-[10px] tracking-widest text-white border border-avail/60 bg-avail/15">
          OPEN LIVE BADGE <ExternalLink className="h-3 w-3" />
        </div>
      </div>
    </a>
  );
}

function SwitchRow({ testId, icon: Icon, title, subtitle, checked, onCheckedChange, tone = "cyan" }) {
  const toneCls = {
    green: {
      wrap: checked ? "border-avail/50 bg-avail/[0.06]" : "border-white/10 bg-white/[0.02]",
      icon: checked ? "text-avail" : "text-white/40",
      chip: checked ? "bg-avail/15 text-avail border-avail/40" : "bg-white/5 text-white/40 border-white/10",
      label: checked ? "AVAILABLE" : "OFF",
    },
    cyan: {
      wrap: checked ? "border-cyan/50 bg-cyan/[0.05]" : "border-white/10 bg-white/[0.02]",
      icon: checked ? "text-cyan" : "text-white/40",
      chip: checked ? "bg-cyan/15 text-cyan border-cyan/40" : "bg-white/5 text-white/40 border-white/10",
      label: checked ? "LIVE" : "OFF",
    },
  }[tone];

  return (
    <div
      data-testid={testId ? `${testId}-row` : undefined}
      className={`rounded-lg border px-3.5 py-3 transition-colors ${toneCls.wrap}`}
    >
      <div className="flex items-start gap-3">
        <div className={`p-1.5 rounded-md border border-white/10 ${toneCls.icon}`} aria-hidden="true">
          <Icon className="h-4 w-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="text-sm font-medium text-white">{title}</div>
            <span className={`mono text-[9px] tracking-[0.18em] uppercase px-2 py-0.5 rounded-full border ${toneCls.chip}`}>
              {toneCls.label}
            </span>
          </div>
          <div className="text-[11px] text-white/50 mt-1 leading-relaxed">{subtitle}</div>
        </div>
        <Switch
          data-testid={testId}
          checked={checked}
          onCheckedChange={onCheckedChange}
          className={checked ? "data-[state=checked]:bg-avail" : ""}
        />
      </div>
    </div>
  );
}

